import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { eventAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Calendar, 
  Plus,
  Search,
  MapPin,
  Clock,
  Users,
  ArrowLeft,
  MoreVertical,
  Trash2,
  Edit,
  Copy,
  CopyPlus,
  ExternalLink,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { PagerBar, usePagedList } from '../components/PagerBar';

export default function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await eventAPI.list();
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const requestDelete = (event, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTarget({ id: event.id, title: event.title });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await eventAPI.delete(deleteTarget.id);
      const removed = res?.data?.removed_attendees ?? 0;
      const fb = res?.data?.removed_feedback ?? 0;
      toast.success(
        `Event deleted${removed || fb ? ` (also removed ${removed} attendee(s) & ${fb} feedback record(s))` : ''}`
      );
      setDeleteTarget(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const copyInviteLink = (inviteCode, e) => {
    e.preventDefault();
    e.stopPropagation();
    const link = `${window.location.origin}/register/${inviteCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied!');
  };

  const handleDuplicate = async (eventId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await eventAPI.duplicate(eventId);
      toast.success('Event duplicated — edit to adjust the date');
      navigate(`/events/${res.data.id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate event');
    }
  };

  const matcher = React.useCallback((event) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesText =
      !q ||
      event.title.toLowerCase().includes(q) ||
      (event.venue || event.location || '').toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    return matchesText && matchesStatus;
  }, [searchQuery, statusFilter]);

  const paged = usePagedList(events, matcher, 12);
  const filteredEvents = paged.items;

  const statusCounts = events.reduce((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'bg-sky-100 text-sky-700 border-sky-200';
      case 'active':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'completed':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  return (
    <Layout activeNav="events">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">Events</h1>
            <p className="text-muted-foreground">Manage your educational events</p>
          </div>
          <Link to="/events/new">
            <Button className="gap-2" data-testid="new-event-btn">
              <Plus className="w-4 h-4" />
              New Event
            </Button>
          </Link>
        </div>

        {/* Search + Status filter */}
        <div className="grid sm:grid-cols-[1fr_220px] gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-events-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-12" data-testid="events-status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses ({events.length})</SelectItem>
              <SelectItem value="upcoming">Upcoming ({statusCounts.upcoming || 0})</SelectItem>
              <SelectItem value="active">Active ({statusCounts.active || 0})</SelectItem>
              <SelectItem value="completed">Completed ({statusCounts.completed || 0})</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : filteredEvents.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Calendar className="w-16 h-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {searchQuery ? 'No events found' : 'No events yet'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery ? 'Try a different search term' : 'Create your first CPD event to get started'}
              </p>
              {!searchQuery && (
                <Link to="/events/new">
                  <Button className="gap-2">
                    <Plus className="w-4 h-4" />
                    Create Event
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="events-grid">
            {filteredEvents.map((event) => (
              <Link key={event.id} to={`/events/${event.id}`}>
                <Card className="ticket-card event-card h-full border-border hover:border-primary/30 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(event.status)}`}>
                        {event.status}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/events/${event.id}/edit`);
                          }}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => copyInviteLink(event.invite_code, e)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy Invite Link
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => handleDuplicate(event.id, e)}
                            data-testid={`duplicate-event-${event.id}`}
                          >
                            <CopyPlus className="w-4 h-4 mr-2" />
                            Duplicate Event
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={(e) => requestDelete(event, e)}
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <h3 className="text-lg font-semibold text-foreground mb-3 line-clamp-2">
                      {event.title}
                    </h3>

                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{format(parseISO(event.date), 'EEEE, MMMM d, yyyy')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{event.time} • {event.duration_hours} hours</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{event.location}</span>
                      </div>
                    </div>

                    {event.learning_objectives?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-xs text-muted-foreground">
                          {event.learning_objectives.length} learning objective{event.learning_objectives.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && paged.total > 0 && (
          <PagerBar
            total={paged.total}
            page={paged.page}
            pageSize={paged.pageSize}
            onPageChange={paged.setPage}
            testid="events-pager"
          />
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-event-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete this event?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                You are about to permanently delete:
              </span>
              <span className="block font-medium text-foreground">
                {deleteTarget?.title}
              </span>
              <span className="block">
                This will also remove all associated <strong>attendees</strong>,
                <strong> feedback responses</strong>, and <strong>sponsorship invitations</strong>
                {' '}for this event. Audit logs are kept for compliance.
              </span>
              <span className="block text-destructive font-medium">
                This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-event-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              data-testid="delete-event-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" />Delete event</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
